"use client";

/**
 * MobileLanding — public homepage on phones (<lg).
 *
 * 1:1 implementation of `guydeez_claude_design/components/21-mobile-onboarding.jsx`
 * (artboard "1. Mobile · Homepage"). Desktop is rendered by DesktopHomeRedesign
 * on the same page and gated by Tailwind `hidden lg:block` / `block lg:hidden`
 * — so this component only ever paints on mobile viewports.
 *
 * Sections (top → bottom):
 *  1. Hero video + search card + ratings pill
 *  2. Trust strip (4 reassurance tiles, 2×2)
 *  3. "Comment ça marche" (4 steps, 2×2)
 *  4. "Villes captivantes" horizontal carousel (real cities)
 *  5. "Coups de cœur de la rédaction" horizontal carousel (placeholders —
 *     no editorial endpoint yet)
 *  6. "Catégories" asymmetric mosaic (real categories)
 *  7. Testimonials carousel (placeholders + real aggregate)
 *  8. ServiceCTA "VOUS HÉSITEZ ?"
 */

import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useParams } from "next/navigation";
import MoneyFromEur from "@/components/MoneyFromEur";
import { useTranslation } from "react-i18next";
import { FeaturedCity, TourCategory, TourDetails } from "@/data/types";
import { useAppDispatch, useAppSelector } from "@/app/lib/hooks";
import { getFeaturedTours } from "@/app/lib/reducers/toursReducer";
import { useCdnUrl } from "@/hooks/useCdnUrl";
import { useReviewsAggregate } from "./desktop/useReviewsAggregate";
import MobileSearchSheet from "../../(client-components)/(Header)/MobileSearchSheet";
import { openLiveChat } from "@/components/liveChat/liveChatClient";
import { getRandomReviews } from "@/app/lib/reducers/reviewsReducer";
import { getContinents } from "@/app/lib/reducers/continentReducer";
import { getCountries } from "@/app/lib/reducers/countriesReducer";

interface Props {
  cities: FeaturedCity[];
  categories: TourCategory[];
  // Server-rendered faux "X travellers right now" count. Computed once on
  // the server (see `(chrome)/page.tsx`) so the number is baked into the
  // SSR HTML — no JS-visible randomness, no hydration drift.
  liveTravellersCount: number;
}

const HERO_VIDEO_POSTER =
  "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=900&q=70";
// Local copy of the Mixkit hero clip — keeps the request same-origin
// (no CSP `media-src` exemption needed) and lets Next.js serve it with
// the standard /public static-asset caching headers.
const HERO_VIDEO_SRC = "/videos/hero-mixkit-4434-720.mp4";

// PNG flags (/public/images/flags/{iso}.png) render consistently across
// all platforms — emoji flags don't on Windows / some Android keyboards.
const flagSrc = (iso?: string): string | null => {
  if (!iso || iso.length !== 2) return null;
  return `/images/flags/${iso.toLowerCase()}.png`;
};

// CMS values / DB names occasionally arrive lowercased ("paris", "new york");
// we title-case on the display side only — the source data stays untouched
// for routing / slugs.
const titleCase = (s?: string): string => {
  if (!s) return "";
  return s
    .split(" ")
    .map((w) =>
      w
        .split("-")
        .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : ""))
        .join("-"),
    )
    .join(" ");
};

const MobileLanding: FC<Props> = ({ cities, categories, liveTravellersCount }) => {
  const { t } = useTranslation("guydeez");
  const { lang, currency } = useParams<{ lang: string; currency: string }>();
  const reviewsAggregate = useReviewsAggregate();

  const localeTag = (lang || "fr_FR").replace("_", "-");
  const fmt = useMemo(() => new Intl.NumberFormat(localeTag), [localeTag]);

  const ratingValue = reviewsAggregate?.avgRating?.toLocaleString(localeTag, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const totalReviewsFmt = reviewsAggregate?.totalReviews
    ? fmt.format(reviewsAggregate.totalReviews)
    : null;

  const [searchOpen, setSearchOpen] = useState(false);
  const openSearch = () => setSearchOpen(true);
  const closeSearch = () => setSearchOpen(false);

  return (
    <main className="gdx block lg:hidden" style={{ background: "white" }}>
      <Hero
        lang={lang}
        currency={currency}
        ratingValue={ratingValue}
        totalReviews={totalReviewsFmt}
        liveTravellersCount={liveTravellersCount}
        onOpenSearch={openSearch}
        t={t}
      />
      <TrustStrip t={t} />
      <HowItWorks t={t} />
      <DestinationsCarousel cities={cities} lang={lang} currency={currency} t={t} />
      <EditorialPicks lang={lang} currency={currency} t={t} />
      <CategoriesMosaic categories={categories} lang={lang} currency={currency} t={t} />
      <Testimonials ratingValue={ratingValue} totalReviews={totalReviewsFmt} t={t} />
      <ServiceCTA lang={lang} currency={currency} t={t} />

      {searchOpen && <MobileSearchSheet onClose={closeSearch} />}
    </main>
  );
};

/* ------------------------------------------------------------------ */
/* Section 1 — Hero                                                   */
/* ------------------------------------------------------------------ */

// Hero background video. iOS Low Power Mode disables video autoplay even for
// muted/playsInline clips, and Safari then paints a native "tap to play"
// button over the <video>. Since the hero is decorative we don't want that:
// a poster <img> always covers the area, and if autoplay is refused we
// unmount the <video> so only the poster (no play button) remains.
const HeroVideo: FC = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [blocked, setBlocked] = useState(false);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const attempt = v.play();
    if (attempt && typeof attempt.then === "function") {
      attempt.catch(() => setBlocked(true));
    }
  }, []);
  const fill: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    zIndex: 0,
  };
  return (
    <>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={HERO_VIDEO_POSTER} alt="" aria-hidden="true" style={fill} />
      {!blocked && (
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={HERO_VIDEO_POSTER}
          style={fill}
        >
          <source src={HERO_VIDEO_SRC} type="video/mp4" />
        </video>
      )}
    </>
  );
};

const Hero: FC<{
  lang: string;
  currency: string;
  ratingValue?: string;
  totalReviews: string | null;
  liveTravellersCount: number;
  onOpenSearch: () => void;
  t: any;
}> = ({
  lang,
  currency,
  ratingValue,
  totalReviews,
  liveTravellersCount,
  onOpenSearch,
  t,
}) => (
  <section
    style={{
      position: "relative",
      height: 520,
      overflow: "hidden",
      color: "white",
    }}
  >
    <HeroVideo />
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 1,
        background:
          "linear-gradient(180deg, rgba(20,75,55,.55) 0%, rgba(10,45,32,.85) 100%)",
      }}
    />
    {/* Bottom fade-out into the next section's white background — design
        canvas does the same so the hero blends rather than hard-cuts. */}
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: 80,
        zIndex: 1,
        background:
          "linear-gradient(180deg, transparent, white 95%)",
      }}
    />

    <div
      style={{
        position: "relative",
        zIndex: 2,
        padding: "24px 18px",
        textAlign: "center",
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 12px",
          borderRadius: 999,
          background: "rgba(255,255,255,.16)",
          color: "white",
          border: "1px solid rgba(255,255,255,.3)",
          fontSize: 11,
          fontWeight: 600,
          marginBottom: 14,
          backdropFilter: "blur(6px)",
        }}
      >
        <span style={{ color: "var(--g-coral-soft)" }} aria-hidden>
          ✦
        </span>
        {t("mobile_home_hero_pill", {
          defaultValue: "+12 000 voyageurs accompagnés",
        })}
      </span>

      <h1
        style={{
          fontSize: 34,
          lineHeight: 1.05,
          fontWeight: 800,
          letterSpacing: "-0.02em",
          marginBottom: 12,
        }}
      >
        {t("mobile_home_hero_title_pre", { defaultValue: "Un guide local," })}
        <br />
        <span style={{ color: "var(--g-coral-soft)", fontStyle: "italic" }}>
          {t("mobile_home_hero_title_em", {
            defaultValue: "une ville qui vous appartient.",
          })}
        </span>
      </h1>

      <p style={{ fontSize: 13, opacity: 0.9, marginBottom: 16, lineHeight: 1.5 }}>
        {t("mobile_home_hero_subtitle", {
          defaultValue:
            "Réservez en 90 secondes une visite privée sur-mesure avec un Guydeez.",
        })}
      </p>

      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 12px",
          background: "rgba(255,255,255,.14)",
          backdropFilter: "blur(10px)",
          borderRadius: 999,
          border: "1px solid rgba(255,255,255,.2)",
          marginBottom: 14,
        }}
      >
        <span style={{ position: "relative", width: 7, height: 7 }}>
          <span
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: "#7EE6A0",
            }}
          />
          {/* Pulsing halo — `gdx-pulse-ring` keyframes live in globals.css
              (scale 1→2.2 + opacity .35→0, infinite). */}
          <span
            style={{
              position: "absolute",
              inset: -3,
              borderRadius: "50%",
              background: "#7EE6A0",
              opacity: 0.35,
              animation: "gdx-pulse-ring 1.6s ease-out infinite",
            }}
          />
        </span>
        <span style={{ fontSize: 11, fontWeight: 600 }}>
          <strong style={{ fontWeight: 800 }}>
            {t("mobile_home_live_count", {
              count: liveTravellersCount,
              defaultValue: `${liveTravellersCount} voyageurs`,
            })}
          </strong>{" "}
          {t("mobile_home_live_suffix", { defaultValue: "en ce moment" })}
        </span>
      </div>

      {/* Search card — taps anywhere open the slide-down search sheet. */}
      <div
        style={{
          padding: 8,
          background: "rgba(255,255,255,.96)",
          borderRadius: 16,
          boxShadow: "0 20px 50px rgba(0,0,0,.4)",
        }}
      >
        <button
          type="button"
          onClick={onOpenSearch}
          style={{
            display: "block",
            width: "100%",
            padding: "10px 14px",
            textAlign: "left",
            marginBottom: 6,
            background: "transparent",
            border: "none",
            cursor: "pointer",
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "var(--g-ink-500)",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              marginBottom: 2,
            }}
          >
            {t("mobile_home_destination_label", { defaultValue: "Destination" })}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "var(--g-burgundy)",
            }}
          >
            <span style={{ color: "var(--g-coral)" }} aria-hidden>
              📍
            </span>
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              {t("mobile_home_destination_placeholder", {
                defaultValue: "Choisissez votre destination",
              })}
            </span>
          </div>
        </button>
        <button
          type="button"
          onClick={onOpenSearch}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            width: "100%",
            height: 46,
            background: "var(--g-green)",
            color: "white",
            borderRadius: 12,
            fontWeight: 700,
            fontSize: 14,
            border: "none",
            cursor: "pointer",
          }}
        >
          <span aria-hidden>🔍</span>{" "}
          {t("mobile_home_search_cta", { defaultValue: "Trouver mon guide" })}
        </button>
      </div>

      {ratingValue && totalReviews && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 14px",
            marginTop: 14,
            background: "rgba(255,255,255,.95)",
            borderRadius: 999,
            color: "var(--g-burgundy)",
          }}
        >
          <Stars n={5} />
          <span style={{ fontWeight: 800, fontSize: 13 }}>{ratingValue}/5</span>
          <span style={{ fontSize: 11, color: "var(--g-ink-500)" }}>
            · {totalReviews} {t("mobile_home_reviews_word", { defaultValue: "avis" })}
          </span>
        </div>
      )}
    </div>
  </section>
);

/* ------------------------------------------------------------------ */
/* Section 2 — Trust strip                                            */
/* ------------------------------------------------------------------ */

const TrustStrip: FC<{ t: any }> = ({ t }) => {
  const dispatch = useAppDispatch();
  const selectedLanguage =
    useAppSelector((s: any) => s?.language?.selectedLanguage) || "fr_FR";
  const continents =
    useAppSelector((s: any) => s?.continent?.continents) || [];
  const countries =
    useAppSelector((s: any) => s?.countries?.countries) || [];

  // Same fetch pattern as desktop SectionExploreGlobe — lazy-load
  // continents + countries if the slices are empty.
  useEffect(() => {
    if (!continents.length) (dispatch as any)(getContinents(true, selectedLanguage));
    if (!countries.length) (dispatch as any)(getCountries(null, true, selectedLanguage));
  }, [dispatch, selectedLanguage, continents.length, countries.length]);

  // Derive live counts (activated only) like the desktop globe section.
  const activeCountries = countries.filter((c: any) => c.activated);
  const totalCountries = activeCountries.length;
  const totalContinents = continents.filter((c: any) => c.activated).length;
  const totalCities = activeCountries.reduce(
    (acc: number, c: any) => acc + (c.numCities || 0),
    0,
  );

  // "X+ destinations" reads as a friendlier round number. We floor the
  // total cities to the nearest 100 (or 10 below 100) so it stays
  // promotional without overpromising — e.g. 309 → 300+, 76 → 70+.
  const roundedDestinations =
    totalCities >= 100
      ? `${Math.floor(totalCities / 100) * 100}+`
      : totalCities >= 10
        ? `${Math.floor(totalCities / 10) * 10}+`
        : totalCities > 0
          ? `${totalCities}`
          : "—";

  const items = [
    {
      key: "cancel",
      emoji: "🛡️",
      label: t("mobile_home_trust_cancel_label", {
        defaultValue: "Annulation gratuite",
      }),
      sub: t("mobile_home_trust_cancel_sub", {
        defaultValue: "jusqu'à 24h avant",
      }),
    },
    {
      key: "pay",
      emoji: "🔒",
      label: t("mobile_home_trust_pay_label", {
        defaultValue: "Paiement sécurisé",
      }),
      sub: t("mobile_home_trust_pay_sub", {
        defaultValue: "Stripe · 3D Secure",
      }),
    },
    {
      key: "dest",
      emoji: "🌐",
      label: t("mobile_home_trust_dest_label", {
        destinations: roundedDestinations,
        defaultValue: `${roundedDestinations} destinations`,
      }),
      sub: t("mobile_home_trust_dest_sub", {
        countries: totalCountries,
        continents: totalContinents,
        defaultValue: `${totalCountries} pays · ${totalContinents} continents`,
      }),
    },
    {
      key: "support",
      emoji: "💬",
      label: t("mobile_home_trust_support_label", {
        defaultValue: "Support 7j/7",
      }),
      sub: t("mobile_home_trust_support_sub", {
        defaultValue: "Des agents humains multilingues",
      }),
    },
  ];
  return (
    <section
      style={{
        background: "white",
        padding: "16px 14px 8px",
        borderBottom: "1px solid var(--g-line)",
      }}
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {items.map((p) => (
          <div
            key={p.key}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 10px",
              background: "var(--g-cream)",
              borderRadius: 10,
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: "white",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                fontSize: 14,
              }}
              aria-hidden
            >
              {p.emoji}
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--g-burgundy)",
                  lineHeight: 1.1,
                }}
              >
                {p.label}
              </div>
              <div style={{ fontSize: 9.5, color: "var(--g-ink-500)", marginTop: 1 }}>
                {p.sub}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* Section 3 — How it works                                           */
/* ------------------------------------------------------------------ */

const HowItWorks: FC<{ t: any }> = ({ t }) => {
  const steps = [
    {
      n: 1,
      title: t("mobile_home_step1_title", { defaultValue: "Réservez" }),
      desc: t("mobile_home_step1_desc", {
        defaultValue: "Choisissez votre destination en 90 secondes.",
      }),
      bg: "var(--g-coral-soft)",
      accent: "var(--g-coral)",
      emoji: "🧳",
    },
    {
      n: 2,
      title: t("mobile_home_step2_title", { defaultValue: "Vérifiez" }),
      desc: t("mobile_home_step2_desc", {
        defaultValue: "Suivez depuis « Mes réservations ».",
      }),
      bg: "#E8F8EE",
      accent: "var(--g-green-deep)",
      emoji: "📱",
    },
    {
      n: 3,
      title: t("mobile_home_step3_title", { defaultValue: "Personnalisez" }),
      desc: t("mobile_home_step3_desc", {
        defaultValue: "Chattez avec votre guide avant le jour J.",
      }),
      bg: "var(--g-cream-deep)",
      accent: "var(--g-burgundy)",
      emoji: "💬",
    },
    {
      n: 4,
      title: t("mobile_home_step4_title", { defaultValue: "Profitez !" }),
      desc: t("mobile_home_step4_desc", {
        defaultValue: "Vivez la ville comme un habitant.",
      }),
      bg: "#FFE8C7",
      accent: "#E08B2E",
      emoji: "🍹",
    },
  ];
  return (
    <section style={{ padding: "28px 18px", background: "white" }}>
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <span
          style={{
            color: "var(--g-coral)",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.14em",
          }}
        >
          {t("mobile_home_how_eyebrow", { defaultValue: "COMMENT ÇA MARCHE" })}
        </span>
        <h2
          style={{
            fontSize: 22,
            color: "var(--g-burgundy)",
            fontWeight: 800,
            marginTop: 6,
            lineHeight: 1.15,
            fontFamily: "var(--g-font-display)",
          }}
        >
          {t("mobile_home_how_title_pre", { defaultValue: "Du clic au départ," })}{" "}
          <span style={{ color: "var(--g-green-deep)" }}>
            {t("mobile_home_how_title_em", { defaultValue: "simple comme bonjour" })}
          </span>
        </h2>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {steps.map((s) => (
          <div
            key={s.n}
            style={{
              padding: "14px 12px",
              background: "white",
              borderRadius: 14,
              border: "1px solid var(--g-line)",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: 46,
                height: 46,
                borderRadius: "50%",
                background: s.bg,
                display: "grid",
                placeItems: "center",
                fontSize: 22,
                position: "relative",
              }}
            >
              <span aria-hidden>{s.emoji}</span>
              <span
                style={{
                  position: "absolute",
                  top: -3,
                  right: -3,
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  background: "white",
                  display: "grid",
                  placeItems: "center",
                  fontWeight: 800,
                  fontSize: 10,
                  color: s.accent,
                  boxShadow: "0 2px 6px rgba(0,0,0,.1)",
                }}
              >
                {s.n}
              </span>
            </div>
            <div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 800,
                  color: "var(--g-burgundy)",
                }}
              >
                {s.title}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--g-ink-500)",
                  lineHeight: 1.4,
                  marginTop: 2,
                }}
              >
                {s.desc}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* Section 4 — Destinations carousel                                  */
/* ------------------------------------------------------------------ */

const DestinationsCarousel: FC<{
  cities: FeaturedCity[];
  lang: string;
  currency: string;
  t: any;
}> = ({ cities, lang, currency, t }) => {
  if (!cities?.length) return null;
  return (
    <section style={{ padding: "24px 0 32px", background: "var(--g-cream)" }}>
      <div style={{ padding: "0 18px 14px" }}>
        <span
          style={{
            color: "var(--g-coral)",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.14em",
          }}
        >
          {t("mobile_home_destinations_eyebrow", {
            defaultValue: "DESTINATIONS POPULAIRES",
          })}
        </span>
        <h2
          style={{
            fontSize: 22,
            color: "var(--g-burgundy)",
            fontWeight: 800,
            marginTop: 6,
            lineHeight: 1.15,
            fontFamily: "var(--g-font-display)",
          }}
        >
          {t("mobile_home_destinations_title_pre", { defaultValue: "Villes" })}{" "}
          <span style={{ color: "var(--g-green-deep)" }}>
            {t("mobile_home_destinations_title_em", { defaultValue: "captivantes" })}
          </span>
        </h2>
      </div>
      <div
        style={{
          display: "flex",
          gap: 12,
          overflowX: "auto",
          // Padding-right is eaten by some browsers when content overflows
          // horizontally — use 0 here and flank with 18-px spacer divs so
          // the gutter is always visible at both scroll ends.
          padding: "4px 0 8px",
          scrollSnapType: "x mandatory",
          // Make scroll-snap respect the 18-px spacers at both ends —
          // without these, snap pulls the first card flush to the left
          // edge as soon as the user nudges horizontally.
          scrollPaddingLeft: 18,
          scrollPaddingRight: 18,
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div style={{ flex: "0 0 18px", width: 18, minWidth: 18 }} aria-hidden />
        {cities.map((c) => {
          const rawName = c?.entry?.data?.[0]?.title || c.name;
          const name = titleCase(rawName);
          const flag = flagSrc((c as any).countryInfo?.code);
          const img = c.thumbnail;
          const tours = c.totalTours ?? 0;
          // Average rating of the city's tours (from the backend). Coerce because
          // the driver may hand it back as a numeric string. Language-independent.
          // No rating (null / 0 / absent) => hidden.
          const ratingNum = Number(c.avgRating);
          const rating =
            Number.isFinite(ratingNum) && ratingNum > 0 ? ratingNum.toFixed(1) : null;
          return (
            <Link
              key={c.id}
              href={`/${lang}/${currency}/city/${c.href.split("/").pop()}` as any}
              style={{
                display: "block",
                width: 240,
                flexShrink: 0,
                scrollSnapAlign: "start",
                background: "white",
                borderRadius: 14,
                overflow: "hidden",
                border: "1px solid var(--g-cream-deep)",
                textDecoration: "none",
                color: "inherit",
              }}
            >
              <div
                style={{
                  position: "relative",
                  height: 160,
                  backgroundImage: img ? `url("${encodeURI(img)}")` : undefined,
                  backgroundColor: "#4a6f5a",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                {tours > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: 10,
                      left: 10,
                      padding: "4px 10px",
                      borderRadius: 999,
                      background: "rgba(255,255,255,.95)",
                      color: "var(--g-burgundy)",
                      fontSize: 10,
                      fontWeight: 700,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    {flag && (
                      <img
                        src={flag}
                        alt=""
                        width={16}
                        height={11}
                        style={{
                          display: "block",
                          borderRadius: 2,
                          objectFit: "cover",
                        }}
                      />
                    )}
                    {tours} {t("mobile_home_card_tours_word", { defaultValue: "tours" })}
                  </span>
                )}
              </div>
              <div style={{ padding: 14 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 4,
                  }}
                >
                  <h3
                    style={{
                      fontSize: 16,
                      color: "var(--g-burgundy)",
                      fontWeight: 800,
                      margin: 0,
                    }}
                  >
                    {name}
                  </h3>
                  {rating && (
                    <span
                      aria-label={rating}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 3,
                        flexShrink: 0,
                        marginLeft: 8,
                        fontSize: 13,
                        fontWeight: 700,
                        color: "var(--g-burgundy)",
                      }}
                    >
                      <span style={{ lineHeight: 1 }} aria-hidden>
                        ★
                      </span>
                      {rating}
                    </span>
                  )}
                </div>
                {(() => {
                  // Localized country name from the joined CMS entry —
                  // falls back to the English raw name when no translation
                  // exists for the active language.
                  const ci: any = (c as any).countryInfo;
                  const countryDisplay =
                    ci?.entry?.data?.[0]?.title ||
                    ci?.nameTranslated ||
                    ci?.name ||
                    "";
                  if (!countryDisplay) return null;
                  return (
                    <div style={{ fontSize: 11, color: "var(--g-ink-500)" }}>
                      {countryDisplay}
                    </div>
                  );
                })()}
              </div>
            </Link>
          );
        })}
        <div style={{ flex: "0 0 18px", width: 18, minWidth: 18 }} aria-hidden />
      </div>
    </section>
  );
};


/* ------------------------------------------------------------------ */
/* Section 5 — Editorial picks (real data, parity with desktop)       */
/* ------------------------------------------------------------------ */

const localizedTourName = (tour: TourDetails): string => {
  const title = (tour.entry as any)?.data?.[0]?.title;
  if (title && String(title).trim() && title !== tour.slug) {
    return String(title).trim();
  }
  return tour.name || "";
};

const PLACEHOLDER_LOCALE_SUFFIX = /\s[a-z]{2}_[A-Z]{2}\s*$/;
const localizedCategoryName = (c: {
  nameTranslated?: string;
  name: string;
}): string => {
  const translated = c.nameTranslated?.trim();
  if (!translated) return c.name;
  if (PLACEHOLDER_LOCALE_SUFFIX.test(translated)) return c.name;
  return translated;
};

const formatDurations = (tour: TourDetails): string | null => {
  const list = (tour.durations || [])
    .filter((d: any) => d.activated !== false)
    .map((d: any) => String(d.duration ?? "").trim())
    .filter(Boolean);
  if (!list.length) return null;
  return list.map((d) => `${d}h`).join(" / ");
};

const EditorialPicks: FC<{ lang: string; currency: string; t: any }> = ({
  lang,
  currency,
  t,
}) => {
  const dispatch = useAppDispatch();
  const selectedLanguage =
    useAppSelector((s: any) => s?.language?.selectedLanguage) ||
    (lang ? String(lang) : "fr_FR");
  const tours: TourDetails[] =
    useAppSelector((s: any) => s?.tours?.featured) || [];
  const toCdnUrl = useCdnUrl();

  useEffect(() => {
    if (!tours.length) {
      (dispatch as any)(getFeaturedTours(selectedLanguage));
    }
  }, [dispatch, selectedLanguage, tours.length]);

  const picks = tours.slice(0, 5);
  if (!picks.length) return null;

  return (
    <section style={{ padding: "32px 0", background: "white" }}>
      <div style={{ padding: "0 18px" }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "5px 10px",
            background: "var(--g-burgundy)",
            color: "white",
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.1em",
            marginBottom: 10,
          }}
        >
          ★ {t("mobile_home_editorial_eyebrow", { defaultValue: "SÉLECTION GUYDEEZ" })}
        </div>
        <h2
          style={{
            fontSize: 22,
            color: "var(--g-burgundy)",
            fontWeight: 800,
            lineHeight: 1.1,
            marginBottom: 16,
            fontFamily: "var(--g-font-display)",
          }}
        >
          {t("mobile_home_editorial_title_pre", { defaultValue: "Coups de cœur" })}{" "}
          <span style={{ color: "var(--g-green-deep)", fontStyle: "italic" }}>
            {t("mobile_home_editorial_title_em", { defaultValue: "de la rédaction" })}
          </span>
        </h2>
      </div>
      <div
        style={{
          display: "flex",
          gap: 12,
          overflowX: "auto",
          padding: "4px 0 14px",
          scrollSnapType: "x mandatory",
          scrollPaddingLeft: 18,
          scrollPaddingRight: 18,
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div style={{ flex: "0 0 18px", width: 18, minWidth: 18 }} aria-hidden />
        {picks.map((tour) => {
          const gallery =
            tour.entry?.galleries?.find((x: any) => x.isFeatured) ||
            tour.entry?.galleries?.[0];
          const image = gallery?.path ? toCdnUrl(gallery.path) : "";
          const tourName = localizedTourName(tour);
          const cityName = titleCase(tour.city?.name);
          const dur = formatDurations(tour);
          const price = tour.pricingDetails?.price;
          const rating = tour.rating || 0;
          const reviews = tour.totalRating || 0;
          const showSellOutBadge = (tour as any).likelyToSellOut === true;
          // `tour.slug` is the tours-table column; the route resolves against
          // the separate `tourSlugs` table, whose rows carry a locale suffix
          // ("…-38-en_US"). Linking with the raw column 404'd every card.
          const routableSlug =
            (tour as any)?.slugs?.find?.((s: any) => s?.slug)?.slug || tour.slug;
          const href =
            tour.city?.slug && routableSlug
              ? `/${lang}/${currency}/tour/${tour.city.slug}/${routableSlug}`
              : `/${lang}/${currency}`;
          return (
            <Link
              key={tour.uuid || tour.slug}
              href={href as any}
              style={{
                width: 270,
                flexShrink: 0,
                scrollSnapAlign: "start",
                background: "white",
                borderRadius: 14,
                overflow: "hidden",
                border: "1px solid var(--g-cream-deep)",
                textDecoration: "none",
                color: "inherit",
                display: "block",
              }}
            >
              <div
                style={{
                  position: "relative",
                  height: 160,
                  backgroundImage: image
                    ? `url("${encodeURI(image)}")`
                    : undefined,
                  backgroundColor: "#4a6f5a",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(180deg, rgba(40,15,15,0) 50%, rgba(40,15,15,.55))",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    left: 10,
                    right: 10,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 5,
                  }}
                >
                  {showSellOutBadge && (
                    <span
                      style={{
                        padding: "4px 9px",
                        background: "var(--g-coral)",
                        color: "white",
                        borderRadius: 999,
                        fontSize: 9,
                        fontWeight: 800,
                        letterSpacing: "0.08em",
                      }}
                    >
                      {t("mobile_home_editorial_badge_popular", {
                        defaultValue: "POPULAIRE",
                      })}
                    </span>
                  )}
                  {(tour.categories || []).slice(0, 2).map((c: any) => (
                    <span
                      key={c.uuid}
                      style={{
                        padding: "4px 9px",
                        borderRadius: 999,
                        background: "rgba(255,255,255,0.95)",
                        color: "var(--g-burgundy)",
                        fontSize: 9.5,
                        fontWeight: 700,
                      }}
                    >
                      {localizedCategoryName(c)}
                    </span>
                  ))}
                </div>
                {cityName && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: 10,
                      left: 12,
                      color: "white",
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: "0.05em",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      textShadow: "0 1px 4px rgba(0,0,0,.4)",
                    }}
                  >
                    <svg
                      width="11"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <path d="M12 22s-7-7.6-7-13a7 7 0 1 1 14 0c0 5.4-7 13-7 13z" />
                      <circle cx="12" cy="9" r="2.5" />
                    </svg>
                    {cityName.toUpperCase()}
                  </div>
                )}
              </div>
              <div style={{ padding: 12 }}>
                <h3
                  style={{
                    fontSize: 13,
                    color: "var(--g-burgundy)",
                    fontWeight: 800,
                    lineHeight: 1.3,
                    marginBottom: 8,
                    minHeight: 34,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {tourName}
                </h3>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderTop: "1px solid var(--g-cream-deep)",
                    paddingTop: 8,
                    fontSize: 10,
                    fontWeight: 600,
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 3,
                      color: "var(--g-ink-700)",
                    }}
                  >
                    {rating > 0 && (
                      <>
                        <span style={{ color: "var(--g-coral)" }} aria-hidden>
                          ★
                        </span>
                        {rating.toFixed(2)}{" "}
                        <span style={{ color: "var(--g-ink-500)", fontWeight: 500 }}>
                          ({reviews})
                        </span>
                      </>
                    )}
                    {rating > 0 && dur && " · "}
                    {dur}
                  </span>
                  {price ? (
                    <span>
                      <span style={{ fontSize: 9, color: "var(--g-ink-500)" }}>
                        {t("mobile_home_card_from", { defaultValue: "dès" })}{" "}
                      </span>
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 800,
                          color: "var(--g-burgundy)",
                        }}
                      >
                        <MoneyFromEur eur={price} />
                      </span>
                    </span>
                  ) : null}
                </div>
              </div>
            </Link>
          );
        })}
        <div style={{ flex: "0 0 18px", width: 18, minWidth: 18 }} aria-hidden />
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* Section 6 — Categories mosaic                                      */
/* ------------------------------------------------------------------ */

const MOSAIC_AREAS = ["prive", "groupe", "velo", "gastro", "excur", "street"];

const CategoriesMosaic: FC<{
  categories: TourCategory[];
  lang: string;
  currency: string;
  t: any;
}> = ({ categories, lang, currency, t }) => {
  // Take the first 6 categories and slot them into the design's asymmetric
  // 2-col mosaic. We don't try to map real categories to the design's
  // `prive/groupe/velo/...` semantic slots — order in the admin wins.
  const six = (categories || []).slice(0, 6);
  if (!six.length) return null;

  return (
    <section style={{ padding: "32px 0", background: "var(--g-cream)" }}>
      <div style={{ padding: "0 18px", marginBottom: 16 }}>
        <span
          style={{
            color: "var(--g-coral)",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.14em",
          }}
        >
          {t("mobile_home_categories_eyebrow", { defaultValue: "CATÉGORIES" })}
        </span>
        <h2
          style={{
            fontSize: 22,
            color: "var(--g-burgundy)",
            fontWeight: 800,
            marginTop: 6,
            lineHeight: 1.15,
            fontFamily: "var(--g-font-display)",
          }}
        >
          {t("mobile_home_categories_title_pre", {
            defaultValue: "Chaque voyage commence par un",
          })}{" "}
          <span style={{ color: "var(--g-green-deep)", fontStyle: "italic" }}>
            {t("mobile_home_categories_title_em", { defaultValue: "choix" })}
          </span>
        </h2>
        <p
          style={{
            fontSize: 12,
            color: "var(--g-ink-500)",
            marginTop: 6,
          }}
        >
          {t("mobile_home_categories_subtitle", {
            defaultValue: "Choisissez un thème pour débuter votre aventure.",
          })}
        </p>
      </div>
      <div
        style={{
          padding: "0 18px",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridAutoRows: "120px",
          gap: 10,
          gridTemplateAreas: `"prive velo" "groupe velo" "gastro excur" "gastro street"`,
        }}
      >
        {six.map((c, i) => {
          const area = MOSAIC_AREAS[i];
          // Same fallback chain as desktop SectionTourCategoriesMosaic
          // (`CategoryTile`) so the mobile mosaic surfaces the exact same
          // CMS-curated image, title and short-content per category.
          const cmsData: any = (c as any).entry?.data?.[0];
          const image =
            cmsData?.mainImage ||
            (c as any).entry?.galleries?.[0]?.path ||
            "";
          const name =
            cmsData?.title || c.nameTranslated || c.name || `Catégorie ${i + 1}`;
          const subline = cmsData?.short_content
            ? String(cmsData.short_content).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()
            : "";
          return (
            <Link
              key={c.uuid}
              // Same target as desktop SectionTourCategoriesMosaic — opens
              // the /tours listing pre-filtered by category slug.
              href={`/${lang}/${currency}/tours?cat=${c.slug}` as any}
              style={{
                gridArea: area,
                borderRadius: 12,
                position: "relative",
                overflow: "hidden",
                cursor: "pointer",
                textDecoration: "none",
                backgroundImage: image ? `url("${encodeURI(image)}")` : undefined,
                backgroundColor: "#4a6f5a",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, rgba(40,15,15,.05) 30%, rgba(40,15,15,.7) 100%)",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,.22)",
                  backdropFilter: "blur(8px)",
                  display: "grid",
                  placeItems: "center",
                  border: "1px solid rgba(255,255,255,.35)",
                  color: "white",
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                →
              </div>
              <div
                style={{
                  position: "absolute",
                  left: 12,
                  right: 12,
                  bottom: 12,
                  color: "white",
                }}
              >
                {subline && (
                  <div
                    style={{
                      fontSize: 9,
                      opacity: 0.9,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      marginBottom: 3,
                    }}
                  >
                    {subline}
                  </div>
                )}
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 800,
                    textShadow: "0 2px 12px rgba(0,0,0,.45)",
                    lineHeight: 1.1,
                  }}
                >
                  {name}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* Section 7 — Testimonials                                           */
/* ------------------------------------------------------------------ */

const PALETTE = ["#D88B53", "#4FBF6E", "#C13B5C", "#5B1F3A", "#2E8C4D"];

const Testimonials: FC<{
  ratingValue?: string;
  totalReviews: string | null;
  t: any;
}> = ({ ratingValue, totalReviews, t }) => {
  const dispatch = useAppDispatch();
  const selectedLanguage =
    useAppSelector((s: any) => s?.language?.selectedLanguage) || "fr_FR";
  const reviews =
    useAppSelector((s: any) => s?.reviews?.randomReviews) || [];

  // Same data source as desktop SectionTestimonialsRedesign — fetches once
  // when the slice is empty so this section works on any page that lands
  // here without upstream wiring.
  useEffect(() => {
    if (!reviews.length) {
      (dispatch as any)(getRandomReviews(6, selectedLanguage));
    }
  }, [dispatch, selectedLanguage, reviews.length]);

  const items = reviews.slice(0, 6);
  if (!items.length) return null;

  return (
    <section style={{ padding: "32px 0", background: "var(--g-cream)" }}>
      <div style={{ textAlign: "center", marginBottom: 18, padding: "0 18px" }}>
        <Stars n={5} size={14} />
        {ratingValue && totalReviews && (
          <div style={{ fontSize: 12, marginTop: 6 }}>
            <b style={{ color: "var(--g-burgundy)" }}>{ratingValue} / 5</b> ·{" "}
            {totalReviews}{" "}
            {t("mobile_home_reviews_verified", { defaultValue: "avis vérifiés" })}
          </div>
        )}
      </div>
      <h2
        style={{
          fontSize: 22,
          color: "var(--g-burgundy)",
          fontWeight: 800,
          lineHeight: 1.1,
          marginBottom: 16,
          textAlign: "center",
          padding: "0 18px",
          fontFamily: "var(--g-font-display)",
        }}
      >
        {t("mobile_home_testimonials_title_pre", { defaultValue: "Ils ont vécu" })}{" "}
        <span style={{ fontStyle: "italic", color: "var(--g-green-deep)" }}>
          {t("mobile_home_testimonials_title_em", { defaultValue: "Guydeez" })}
        </span>
      </h2>
      <div
        style={{
          display: "flex",
          gap: 12,
          overflowX: "auto",
          padding: "4px 0 14px",
          scrollSnapType: "x mandatory",
          // Make scroll-snap respect the 18-px spacers at both ends —
          // without these, snap pulls the first card flush to the left
          // edge as soon as the user nudges horizontally.
          scrollPaddingLeft: 18,
          scrollPaddingRight: 18,
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div style={{ flex: "0 0 18px", width: 18, minWidth: 18 }} aria-hidden />
        {items.map((r: any, i: number) => {
          const quote = String(r.comment || "").trim();
          const truncated =
            quote.length > 180 ? quote.slice(0, 180).trimEnd() + "…" : quote;
          const cityLabel = r.cityTranslated ? titleCase(r.cityTranslated) : "";
          const meta = [cityLabel, r.tourTranslated].filter(Boolean).join(" · ");
          return (
            <div
              key={r.uuid || `${r.name}-${i}`}
              style={{
                padding: "18px 16px",
                background: "white",
                borderRadius: 14,
                width: 270,
                flexShrink: 0,
                scrollSnapAlign: "start",
              }}
            >
              <Stars n={r.rating ?? 5} />
              <p
                style={{
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  fontStyle: "italic",
                  margin: "8px 0 12px",
                  color: "var(--g-ink-900)",
                  // Lock the quote area to exactly 5 lines so every card's
                  // author block lines up — too short fills with whitespace,
                  // too long gets ellipsised. lineHeight 1.5 × 12.5 = 18.75
                  // → 5 lines = 93.75 px, so min-height 94 covers it.
                  display: "-webkit-box",
                  WebkitLineClamp: 5,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  minHeight: 94,
                }}
              >
                « {truncated} »
              </p>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  paddingTop: 10,
                  borderTop: "1px solid var(--g-cream-deep)",
                }}
              >
                <Avatar
                  name={r.name || "?"}
                  color={PALETTE[i % PALETTE.length]}
                  size={28}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "var(--g-ink-900)",
                    }}
                  >
                    {r.name}
                  </div>
                  {meta && (
                    <div
                      style={{
                        fontSize: 10,
                        color: "var(--g-ink-500)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {meta}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div style={{ flex: "0 0 18px", width: 18, minWidth: 18 }} aria-hidden />
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* Section 8 — Service CTA                                            */
/* ------------------------------------------------------------------ */

const ServiceCTA: FC<{ lang: string; currency: string; t: any }> = ({
  lang,
  t,
}) => {
  const [opening, setOpening] = useState(false);
  const handleClick = async () => {
    if (opening) return;
    setOpening(true);
    try {
      await openLiveChat(lang);
    } finally {
      setOpening(false);
    }
  };
  return (
    <section
      style={{
        background: "var(--g-burgundy)",
        padding: "28px 18px",
        color: "white",
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: "var(--g-coral-soft)",
          fontWeight: 700,
          letterSpacing: "0.14em",
          marginBottom: 8,
        }}
      >
        {t("mobile_home_advice_eyebrow", { defaultValue: "VOUS HÉSITEZ ?" })}
      </div>
      <h3
        style={{
          fontSize: 20,
          fontWeight: 800,
          marginBottom: 8,
          lineHeight: 1.2,
        }}
      >
        {t("mobile_home_advice_title", {
          defaultValue:
            "Discutez avec un conseiller — réponse en quelques minutes",
        })}
      </h3>
      <p
        style={{
          fontSize: 12.5,
          opacity: 0.85,
          marginBottom: 18,
          lineHeight: 1.55,
        }}
      >
        {t("mobile_home_advice_subtitle", {
          defaultValue:
            "Notre équipe connaît chaque destination et tous nos guides locaux. On vous aide à choisir le bon tour, sans engagement.",
        })}
      </p>
      <button
        type="button"
        onClick={handleClick}
        disabled={opening}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          width: "100%",
          background: "var(--g-coral)",
          color: "white",
          height: 46,
          padding: "0 18px",
          fontWeight: 700,
          fontSize: 13.5,
          borderRadius: 999,
          border: "none",
          cursor: opening ? "wait" : "pointer",
        }}
      >
        💬{" "}
        {opening
          ? t("live_chat_loading", { defaultValue: "Chargement…" })
          : t("mobile_home_advice_cta", {
              defaultValue: "Démarrer la conversation",
            })}
      </button>
    </section>
  );
};


/* ------------------------------------------------------------------ */
/* Tiny primitives — Stars + Avatar                                   */
/* ------------------------------------------------------------------ */

const Stars: FC<{ n?: number; size?: number }> = ({ n = 5, size = 11 }) => (
  <span
    style={{
      display: "inline-flex",
      gap: 1,
      color: "var(--g-coral)",
      fontSize: size,
      lineHeight: 1,
    }}
    aria-hidden
  >
    {Array.from({ length: n }, (_, i) => (
      <span key={i}>★</span>
    ))}
  </span>
);

const Avatar: FC<{ name: string; color: string; size?: number }> = ({
  name,
  color,
  size = 24,
}) => {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: color,
        color: "white",
        display: "grid",
        placeItems: "center",
        fontWeight: 800,
        fontSize: Math.round(size * 0.45),
        flexShrink: 0,
      }}
      aria-hidden
    >
      {initial}
    </div>
  );
};

export default MobileLanding;
